/** Layout, camera and copy constants for the map visual. Kept React-free so
 *  the render-model assert can import the module without a DOM. */

/** Compact-tile metric unit. The value is the pin count. */
export const MAP_METRIC_UNIT = 'PLACES';

/** Padding, in pixels, kept between the fitted bounds and the canvas edge so
 *  markers never sit under the chrome or the attribution line. */
export const MAP_FIT_PADDING = 48;

/** A single pin (or several pins in one building) fits to a degenerate box,
 *  which would otherwise fit to MapLibre's maximum zoom and show a street
 *  corner with no context. */
export const MAP_FIT_MAX_ZOOM = 14;

/** Used only when the canvas has no fitted bounds yet. */
export const MAP_DEFAULT_ZOOM = 11;

/**
 * MapLibre's zoom-interpolating globe projection: a sphere at planet altitude
 * that renders as plain mercator once the camera closes in, so the landed
 * frame reads exactly as it did before the globe existed. Set at runtime
 * (after `style.load`) because the style JSON is an external URL that carries
 * no projection of its own.
 */
export const MAP_GLOBE_PROJECTION = { type: 'globe' } as const;

/** Expanded-open intro flight: the camera waits over the pins at planet
 *  altitude — a visible globe — then flies down onto the fitted frame. Zoom
 *  0.8 keeps most of the sphere in a ~500px canvas without shrinking it to a
 *  marble. */
export const MAP_INTRO_START_ZOOM = 0.8;

/** How long the globe holds after the first tiles land before the descent
 *  begins. Without the beat, a warm-cache load starts the flight while the
 *  shell is still scaling in and the globe is never actually seen. */
export const MAP_INTRO_HOLD_MS = 400;

/** Descent duration. Long enough to read as travel, short enough that a
 *  reader who opened the block for the street view is not held hostage. */
export const MAP_INTRO_FLY_DURATION_MS = 2200;

/**
 * Where the self-hosted MapLibre worker is served from.
 *
 * MapLibre derives its worker URL from `import.meta.url` and returns an EMPTY
 * string whenever that is not an http(s) URL — which is exactly what Turbopack's
 * bundling produces (`maplibre-gl/src/util/web_worker.ts: defaultWorkerUrl`).
 * The map then constructs `new Worker("", {type:"module"})`, which fails before
 * any message is exchanged, and every vector tile request sits in `loading`
 * forever: the style, TileJSON and sprites all return 200 while not one `.pbf`
 * is ever requested, and the canvas paints black under the markers.
 *
 * So the worker bundle is vendored into `apps/fe/public/maplibre/` and pointed
 * at explicitly. `map-worker-asset.assert.ts` pins those copies to the installed
 * package byte-for-byte, because a worker from a different MapLibre build than
 * the main thread is a protocol mismatch with no clean failure mode.
 */
export const MAP_WORKER_URL = '/maplibre/maplibre-gl-worker.mjs';

/**
 * How long the map may take to reach `load` before it is declared unavailable.
 *
 * `load` fires only once the style AND the first viewport tiles are in, so it is
 * the one signal that proves the whole pipeline — main thread, worker, network —
 * is alive; a dead worker fires no error and no load, it simply never finishes.
 * 15s is far past a normal cold load (style + tiles land in low single-digit
 * seconds even on slow connections) and still short enough that a reader is not
 * left staring at a black rectangle.
 */
export const MAP_LOAD_TIMEOUT_MS = 15_000;

/** The shell's enter animation runs 0.32s (`Cmarkdownvisual` canvas transition).
 *  One resize after it settles corrects the canvas for the scale-in, which the
 *  ResizeObserver alone can miss because a transform does not change layout. */
export const MAP_ENTER_SETTLE_MS = 420;

/** DEFAULT floor for the drawn canvas so the map never collapses to a sliver
 *  when the location list is long. An embedder that owns a smaller slot
 *  overrides it with `Cmarkdownmap`'s `canvasMinHeight`. */
export const MAP_CANVAS_MIN_HEIGHT = 260;

/** DEFAULT floor for the expanded body. Matches the treemap/gantt canvases so
 *  a map block is the same height as its neighbours in a deliverable;
 *  `Cmarkdownmap`'s `contentMinHeight` overrides it. */
export const MAP_CONTENT_MIN_HEIGHT = 420;

/** The product ships a single dark surface (see `CmarkdownvisualStyles`), so
 *  the style choice is a constant rather than a theme lookup. Named so the day
 *  a light theme exists there is exactly one place to change. */
export const MAP_SURFACE_IS_DARK = true;

/** Accessible name for a map with no `title:` directive. */
export const MAP_ACCESSIBLE_NAME_FALLBACK = 'Map';

/** Noun after the plotted-pin count in the map's accessible name. */
export const MAP_ACCESSIBLE_NAME_LOCATION_NOUN = 'location';
export const MAP_ACCESSIBLE_NAME_LOCATIONS_NOUN = 'locations';


/** Trailing text on a row whose place has no position and no verdict yet. */
export const MAP_UNRESOLVED_DETAIL_TEXT = 'No position yet';

/** Trailing text for a place the geocoder matched to nothing at all. */
export const MAP_NOT_FOUND_DETAIL_TEXT = 'No match found';

/** Trailing text for a lookup that never completed. Phrased as the lookup's
 *  failure, not the place's — the name may be perfectly good. */
export const MAP_LOOKUP_FAILED_DETAIL_TEXT = 'Lookup failed';

/** Leads the trailing text of a place that matched several real locations. */
export const MAP_AMBIGUOUS_DETAIL_PREFIX = 'Ambiguous';

/** How many rival places a row names before it starts counting them. Three is
 *  what fits beside a label without pushing the row into a paragraph. */
export const MAP_AMBIGUOUS_CANDIDATE_LIMIT = 3;

export const MAP_AMBIGUOUS_CANDIDATE_SEPARATOR = ' · ';

export const MAP_AMBIGUOUS_OVERFLOW_LABEL = 'more';

/** ~11m of precision — enough to identify a spot, short enough to read. */
export const MAP_COORDINATE_FRACTION_DIGITS = 4;
